/**
 * Cascade Delete Types
 * Types for cascade delete functionality with hierarchical dependency visualization
 */

/** Dependency resource type */
export type DependencyType = 'task' | 'connection';

/** Individual dependency item */
export interface Dependency {
  /** Type of the dependent resource */
  type: DependencyType;
  /** Unique identifier of the resource */
  id: string;
  /** Display name of the resource */
  name: string;
}

/** Node in the dependency tree for hierarchical visualization */
export interface DependencyTreeNode {
  /** Type of dependency at this node */
  type: DependencyType;
  /** Items at this node level */
  items: Dependency[];
  /** Child nodes for hierarchical dependencies */
  children?: DependencyTreeNode[];
  /** Whether this group is expanded in the UI */
  isExpanded?: boolean;
}

/** Complete dependency tree structure */
export interface DependencyTree {
  /** Root entity type being deleted */
  rootType: 'partner' | 'connection';
  /** Root entity name */
  rootName: string;
  /** Root entity ID */
  rootId: string;
  /** Child dependency nodes */
  nodes: DependencyTreeNode[];
  /** Total count of all dependencies */
  totalCount: number;
}

/** Deletion stage in the cascade delete process */
export type DeletionStage =
  | 'preparing'
  | 'deleting_tasks'
  | 'deleting_connections'
  | 'deleting_entity'
  | 'cleaning_resources'
  | 'completed'
  | 'failed';

/** Stage metadata for progress display */
export interface DeletionStageInfo {
  /** Stage identifier */
  stage: DeletionStage;
  /** Display label for the stage */
  label: string;
  /** Current count of items processed */
  currentCount?: number;
  /** Total count of items to process */
  totalCount?: number;
  /** Whether this stage is complete */
  isComplete: boolean;
  /** Whether this stage is currently active */
  isActive: boolean;
  /** Error message if stage failed */
  error?: string;
}

/** Progress tracking for cascade delete operations */
export interface CascadeDeleteProgress {
  /** Overall progress percentage (0-100) */
  percentage: number;
  /** Current stage of deletion */
  currentStage: DeletionStage;
  /** All stages with their status */
  stages: DeletionStageInfo[];
  /** Current status message */
  message: string;
  /** Error message if deletion failed */
  error?: string;
  /** Start time of the deletion */
  startTime?: string;
  /** Estimated completion time */
  estimatedCompletionTime?: string;
}

/**
 * Get plural display label for a dependency type
 */
export function getDependencyTypePluralLabel(type: DependencyType): string {
  const labels: Record<DependencyType, string> = {
    task: 'Tasks',
    connection: 'Connections',
  };
  return labels[type];
}
